import { API, deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '@/api/api';
import { mapTemporaryToDto, type TemporaryContract } from '@/store/contracts/contracts.mappers';
import type { InviteEvent } from '@/store/invites/invites.types';

import type {
  ConfirmInviteData,
  InviteDetailsDto,
  InviteEmailTemplate,
  LoginRequest,
  LoginResponse,
  RegistrationConfirmationResponse,
  TwoFactorVerifyResponse,
} from './auth.dto';
import { mapInviteDetails } from './auth.mappers';

/** Code-based 2FA channels — a one-time code is sent and verified. */
export type TwoFactorChallengeMethod = 'email' | 'sms' | 'call';
/** Every selectable 2FA method, including the authenticator-app (TOTP) flow. */
export type TwoFactorMethod = TwoFactorChallengeMethod | 'totp';

// ── Auth account (the login side of the user, separate from the people profile) ──

/**
 * The auth-service record behind the session. Its `phone` is the number every SMS
 * (and voice) 2FA code goes to: auth fills that column from the account *login*
 * only, so a `phone` contact written to the people service never reaches it.
 */
export type AuthAccount = {
  id: string;
  email: string | null;
  phone: string | null;
  /**
   * Whether the account signs in with a password. When it does, sensitive changes (2FA,
   * the login itself) need it again; an OAuth-only account has none to give.
   */
  has_password?: boolean;
  /** Social sign-ins linked to the account (google, apple, …); they find it by its email. */
  oauth_providers?: string[];
};

/** Auth serialises an unset login half as the string "false" (or ""), not null. */
const loginOrNull = (value: string | null | undefined) =>
  value && value !== 'false' ? value : null;

const toAuthAccount = (raw: AuthAccount): AuthAccount => ({
  ...raw,
  email: loginOrNull(raw.email),
  phone: loginOrNull(raw.phone),
});

export const getAuthAccount = async () => {
  const res = await getRequest<AuthAccount>(API.AUTH, '/user/me');
  return toAuthAccount(res.data);
};

/**
 * Step one of adding — or replacing — one half of the account login: an E.164 phone
 * number, or an email address. This is the only way to give a code-based 2FA method a
 * channel to reach, since auth sends to `phone`/`email` on this record and fills them
 * from the login alone. Nothing changes yet: auth texts or emails a 6-digit code to the
 * *new* login and answers with a plain-text token for {@link confirmLoginChange}. The
 * value must not already belong to another account, nor be the current one. Changing
 * one's own login needs the current password when the account has one.
 */
export const requestLoginChange = async (userId: string, login: string, oldPassword?: string) => {
  const res = await putRequest<string, { login: string; old_password?: string }>(
    API.AUTH,
    `/user/${userId}`,
    { login, old_password: oldPassword || undefined },
  );
  return String(res.data).trim();
};

/**
 * Step two: applies the change with the token from {@link requestLoginChange} and the
 * code sent to the new login. The other half of the login is left as it is. A token is
 * single-use — a wrong code burns it, so a retry needs a fresh request. Auth revokes every
 * session of the account once the change lands.
 */
export const confirmLoginChange = async (userId: string, secret: string, code: string) => {
  const res = await patchRequest<AuthAccount, { secret: string; code: string }>(
    API.AUTH,
    `/user/confirm-update/${userId}`,
    { secret, code },
  );
  return toAuthAccount(res.data);
};

/** Signs in with a password without touching the store; the caller keeps the tokens. */
export const signInWithPassword = async (login: string, password: string) => {
  const res = await postRequest<LoginResponse, LoginRequest>(API.AUTH, '/login', {
    login: login.toLowerCase(),
    password,
  });
  return res.data;
};

/**
 * Checks the login is usable for sign-up. Auth may answer 204 for any login (an enumeration
 * guard), so a pass does not mean the login is free: /register still refuses a duplicate.
 * Throws the auth `DUPLICATE` error when auth chooses to say so. Lower-cased to match how
 * /register stores it.
 */
export const checkLogin = async (login: string) => {
  await postRequest<void, { login: string }>(API.AUTH, '/check-login', {
    login: login.toLowerCase(),
  });
};

/**
 * Sets a new password once auth has checked the current one (with its own lockout), and
 * revokes every session of the account, this one included. Auth refuses a new password
 * that matches the current one.
 */
export const updatePassword = async (oldPassword: string, password: string) => {
  await patchRequest<void, { old_password: string; password: string }>(
    API.AUTH,
    '/user/update-password',
    { old_password: oldPassword, password },
  );
};

export const hardDeleteUser = async (userId: string) => {
  await deleteRequest(API.AUTH, `user/remove/${userId}`);
};

export const inviteUser = async (
  contact: string,
  name: string,
  role: string,
  serviceId: string,
  serviceType: string,
  serviceName?: string,
  roleName?: string,
  event?: InviteEvent,
  message?: string,
  temporary?: TemporaryContract,
  /** Guests the invitee brings along; event invitations only. */
  additionalGuests?: number,
  emailTemplate?: InviteEmailTemplate,
) => {
  await postRequest(API.AUTH, '/user/invite', {
    contact: contact.toLowerCase(),
    name,
    role,
    service_id: serviceId,
    service_type: serviceType,
    service_name: serviceName,
    role_name: roleName,
    event,
    message: message || undefined,
    temporary: mapTemporaryToDto(temporary),
    additional_guests: additionalGuests,
    email_template: emailTemplate,
  });
};

export const inviteUsersBulk = async (
  /** `additionalGuests`: the guests each invitee brings along; event invitations only. */
  invitees: { contact: string; name: string; additionalGuests?: number }[],
  role: string,
  serviceId: string,
  serviceType: string,
  serviceName?: string,
  event?: InviteEvent,
  message?: string,
  /** One contract and permission set for the whole batch. */
  temporary?: TemporaryContract,
  emailTemplate?: InviteEmailTemplate,
) => {
  await postRequest(API.AUTH, '/user/invite/bulk', {
    invitees: invitees.map(({ contact, name, additionalGuests }) => ({
      contact: contact.toLowerCase(),
      name,
      additional_guests: additionalGuests,
    })),
    role,
    service_id: serviceId,
    service_type: serviceType,
    service_name: serviceName,
    event,
    message: message || undefined,
    temporary: mapTemporaryToDto(temporary),
    email_template: emailTemplate,
  });
};

// Resolves with what the sign-up form is prefilled with while the invitation can still be
// accepted; does not use the secret up.
export const checkInvite = async (secret: string) => {
  const res = await postRequest<InviteDetailsDto, { secret: string }>(
    API.AUTH,
    '/user/check-invite',
    { secret },
  );
  return mapInviteDetails(res.data);
};

export const confirmInvite = async (data: ConfirmInviteData) => {
  return await postRequest<RegistrationConfirmationResponse, unknown>(
    API.AUTH,
    '/user/confirm-invite',
    { ...data, provider: 'basic' },
  );
};

export const sendMagicLink = async (login: string) => {
  await postRequest(API.AUTH, `/magic-link?login=${encodeURIComponent(login)}`, null);
};

/**
 * Finishes an OAuth sign-in the provider could not vouch for: the token from
 * `/oauth/{provider}/complete` and the code auth emailed to the account. Answers like
 * /login, so a 2FA challenge can still follow.
 */
export const confirmOAuthLogin = async (secret: string, code: string) =>
  postRequest<LoginResponse, { secret: string; code: string }>(API.AUTH, '/oauth/confirm', {
    secret,
    code,
  });

export const confirmMagicLink = async (secret: string) => {
  return await postRequest<LoginResponse, { secret: string }>(API.AUTH, '/confirm-magic-link', {
    secret,
  });
};

// ── 2FA management (authenticated; require a Bearer token) ──────────────────

/**
 * The current password some 2FA changes ask for again. An account without one (OAuth only)
 * sends it empty.
 */
type PasswordStepUp = { password: string };

/**
 * Turn on a code-based 2FA method for the current user. TOTP is enabled via its
 * own QR endpoint (see `enableTotp` in the store), not here.
 */
export const enableTwoFactorMethod = async (method: TwoFactorChallengeMethod) => {
  await postRequest(API.AUTH, `/2fa/enable/${method}`, {});
};

export const disableTwoFactorMethod = async (method: TwoFactorMethod, password: string) => {
  await postRequest<void, PasswordStepUp>(API.AUTH, `/2fa/disable/${method}`, { password });
};

/** Arms TOTP; it stays pending until a code from the app confirms it. */
export const enableTotp = async (password: string) => {
  const res = await postRequest<{ image: string; url: string }, PasswordStepUp>(
    API.AUTH,
    '/2fa/enable/totp',
    { password },
  );
  return res.data;
};

/**
 * Replaces the account's recovery codes; the old ones stop working at once. Auth answers
 * 409 when no 2FA method is on.
 */
export const regenerateRecoveryCodes = async (password: string): Promise<string[]> => {
  const res = await postRequest<{ recovery_codes?: string[] }, PasswordStepUp>(
    API.AUTH,
    '/2fa/recovery-codes',
    { password },
  );
  return res.data?.recovery_codes ?? [];
};

/**
 * Confirm a freshly-enabled 2FA method. Enabling now only arms a method — it stays
 * pending until the user proves possession: the code from their authenticator app
 * (TOTP) or the one-time code sent to their email/phone. Recovery codes are returned
 * once, on the first method the account ever confirms; an empty array otherwise.
 */
export const confirmTwoFactorMethod = async (
  method: TwoFactorMethod,
  code: string,
): Promise<string[]> => {
  const res = await postRequest<{ recovery_codes?: string[] }, { method: string; code: string }>(
    API.AUTH,
    `/2fa/confirm/${method}`,
    { method, code },
  );
  return res.data?.recovery_codes ?? [];
};

export const setDefaultTwoFactorMethod = async (method: TwoFactorMethod, password: string) => {
  await postRequest<void, PasswordStepUp>(API.AUTH, `/2fa/default/${method}`, { password });
};

// ── 2FA login challenge (unauthenticated; carry the token from /login) ───────

/**
 * Send a one-time code to the chosen channel during a login challenge.
 * `token` is the `two_factor_token` returned by /login, /oauth/.../complete or
 * /confirm-magic-link.
 */
export const sendTwoFactorCode = async (method: TwoFactorChallengeMethod, token: string) => {
  await postRequest<void, { method: string; token: string }>(API.AUTH, `/2fa/send/${method}`, {
    method,
    token,
  });
};

/** Verify a code-based 2FA challenge and exchange it for auth tokens. */
export const verifyTwoFactorCode = async (
  method: TwoFactorChallengeMethod,
  token: string,
  code: string,
) =>
  postRequest<TwoFactorVerifyResponse, { method: string; token: string; code: string }>(
    API.AUTH,
    `/2fa/verify/${method}`,
    { method, token, code },
  );

/**
 * Spend a recovery code to bypass a lost 2FA device. On success the backend wipes
 * ALL 2FA methods (totp/sms/call/email/passkey) and returns a fresh token pair.
 * `token` is the two_factor_token from the login challenge.
 */
export const verifyRecoveryCode = async (token: string, code: string) =>
  postRequest<TwoFactorVerifyResponse, { token: string; code: string }>(API.AUTH, '/2fa/recovery', {
    token,
    code,
  });
